import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentAPIResponseMapper } from '@/api/mapper/payment';
import { getPaymentsManagement, getUserPayments } from '@/api/service/payment';

export const paymentKeys = {
  all: ['payments'] as const,
  management: () => [...paymentKeys.all, 'management'] as const,
  byUser: (userId: number | string) => [...paymentKeys.all, 'user', String(userId)] as const,
};

export function usePaymentsManagementQuery(enabled = true) {
  return useQuery({
    queryKey: paymentKeys.management(),
    queryFn: getPaymentsManagement,
    enabled,
  });
}

export function useUserPaymentsQuery(userId?: string | number | null, enabled = true) {
  const id = userId != null && userId !== '' ? Number(userId) : NaN;
  const canFetch = enabled && Number.isFinite(id);

  return useQuery({
    queryKey: paymentKeys.byUser(canFetch ? id : 'unknown'),
    queryFn: async () => {
      const response = await getUserPayments(id);
      return response.data.map(PaymentAPIResponseMapper);
    },
    enabled: canFetch,
  });
}

export function useInvalidatePaymentsManagement() {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: paymentKeys.management(),
      }),
    [queryClient]
  );
}

export function useInvalidateUserPayments() {
  const queryClient = useQueryClient();

  return useCallback(
    (userId?: string | number) =>
      userId != null
        ? queryClient.invalidateQueries({ queryKey: paymentKeys.byUser(userId) })
        : queryClient.invalidateQueries({ queryKey: paymentKeys.all }),
    [queryClient]
  );
}
